import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from '../lib/Icons';

/**
 * Modal shown when users try to access premium content
 * - For guests: Prompts to create account / sign in
 * - For users without subscription: Prompts to start free trial
 */
const SubscriptionModal = ({ isOpen, onClose, activityTitle, user }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const isGuest = !user;

  const handleSignUp = () => {
    navigate('/signup/solo');
  };

  const handleSignIn = () => {
    navigate('/signup/solo?mode=login');
  };

  const handleStartTrial = () => {
    navigate('/solo/checkout');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-purple-600 to-pink-600 p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-white/20 rounded-2xl flex items-center justify-center mb-4">
            {isGuest ? (
              <Icons.Lock className="w-8 h-8 text-white" />
            ) : (
              <Icons.Star className="w-8 h-8 text-white" />
            )}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">
            {isGuest ? 'Create a Free Account' : 'Start Your Free Trial'}
          </h2>
          <p className="text-purple-100">
            {isGuest 
              ? 'Sign up to access all SEN teaching resources'
              : 'Unlock full access to all SEN teaching resources'
            }
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-300 text-center mb-6">
            {isGuest ? (
              <>
                <strong className="text-white">"{activityTitle}"</strong> and hundreds of other 
                resources are waiting for you. Create your free account to get started.
              </>
            ) : (
              <>
                Access <strong className="text-white">"{activityTitle}"</strong> and all our 
                resources with a 7-day free trial. Cancel anytime.
              </>
            )}
          </p>

          {/* Features list */}
          <ul className="space-y-3 mb-6">
            {[
              'Hundreds of evidence-based activities',
              'Video tutorials and implementation guides',
              'AI-powered resource search',
              'Symbol generator and resource builder',
              'New resources added regularly',
            ].map((feature, idx) => (
              <li key={idx} className="flex items-center gap-3 text-slate-300">
                <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Icons.Check className="w-3 h-3 text-green-400" />
                </div>
                {feature}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          {isGuest ? (
            <div className="space-y-3">
              <button
                onClick={handleSignUp}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-purple-500/25"
              >
                Create Free Account
              </button>
              <button
                onClick={handleSignIn}
                className="w-full px-6 py-3 text-slate-300 hover:text-white font-medium transition-colors"
              >
                Already have an account? Sign In
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={handleStartTrial}
                className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-purple-500/25"
              >
                Start 7-Day Free Trial
              </button>
              <p className="text-center text-slate-400 text-sm">
                No credit card required to start
              </p>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
        >
          <Icons.XMark className="w-5 h-5" />
        </button>

        {/* Footer */}
        <div className="px-6 pb-4 text-center">
          <button
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-400 transition-colors"
          >
            ← Back to browsing
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
